<script setup lang="ts">
// Page admin V2 « Dépôt BNP Factor » (Mon activité) — réservée DEV_ADMIN.
// Liste les factures dont le mode de paiement est l'affacturage BNP Factor,
// suit leur cycle de vie chez le factor, et génère les dépôts (xlsx + PDF zip).
import { computed, onMounted, ref } from 'vue'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  DocumentPlusIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilSquareIcon,
} from '@heroicons/vue/24/outline'
import {
  useFactorDepositStore,
  type FactorExportDetail,
  type FactorExportRow,
  type FactorInvoiceRow,
  type FactorInvoiceRowStatus,
  type GenerateFactorExportResult,
} from '@/v2/stores/useFactorDepositStore'
import FactorLifecycleDialog from '@/v2/components/factor/FactorLifecycleDialog.vue'

const store = useFactorDepositStore()

type Tab = 'invoices' | 'exports'
const activeTab = ref<Tab>('invoices')

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

// ── Onglet Factures : filtres + pagination ───────────────────────────────────
const fDateFrom = ref('')
const fDateTo = ref('')
const fStatus = ref<FactorInvoiceRowStatus | ''>('')
const fQuery = ref('')
const page = ref(1)
const pageSize = ref(50)

const pageCount = computed(() => Math.max(1, Math.ceil(store.invoicesTotal / pageSize.value)))
const rangeFrom = computed(() => (store.invoicesTotal === 0 ? 0 : (page.value - 1) * pageSize.value + 1))
const rangeTo = computed(() => Math.min(page.value * pageSize.value, store.invoicesTotal))

function loadInvoices() {
  store.fetchInvoices({
    dateFrom: fDateFrom.value || undefined,
    dateTo: fDateTo.value || undefined,
    status: fStatus.value || undefined,
    q: fQuery.value || undefined,
    page: page.value,
    pageSize: pageSize.value,
  })
}

function applyFilters() {
  page.value = 1
  loadInvoices()
}

let searchTimer: ReturnType<typeof setTimeout> | null = null
function onSearchInput(value: string) {
  fQuery.value = value
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
}

function setPage(p: number) {
  if (p < 1 || p > pageCount.value) return
  page.value = p
  loadInvoices()
}

// ── Cycle de vie ─────────────────────────────────────────────────────────────
const lifecycleTarget = ref<FactorInvoiceRow | null>(null)
const isSavingLifecycle = ref(false)

function openLifecycle(row: FactorInvoiceRow) {
  lifecycleTarget.value = row
}

async function onLifecycleSave(payload: Parameters<typeof store.updateLifecycle>[1]) {
  if (!lifecycleTarget.value) return
  isSavingLifecycle.value = true
  try {
    const updated = await store.updateLifecycle(lifecycleTarget.value.invoiceUuid, payload)
    if (updated) lifecycleTarget.value = null
  } finally {
    isSavingLifecycle.value = false
  }
}

// ── Onglet Exports : génération ──────────────────────────────────────────────
const gDateFrom = ref(isoDaysAgo(30))
const gDateTo = ref(isoDaysAgo(0))
const forceReexport = ref(false)
const generateError = ref('')
const result = ref<GenerateFactorExportResult | null>(null)

const rangeInvalid = computed(() => !gDateFrom.value || !gDateTo.value || gDateFrom.value > gDateTo.value)

async function generate() {
  if (rangeInvalid.value || store.isGenerating) return
  generateError.value = ''
  result.value = null
  const res = await store.generate(gDateFrom.value, gDateTo.value, forceReexport.value)
  if (res.ok && res.data) {
    result.value = res.data
    await store.fetchExports()
    if (activeTab.value === 'invoices') loadInvoices()
  } else {
    generateError.value = res.message ?? 'Échec de la génération'
  }
}

// ── Téléchargements ──────────────────────────────────────────────────────────
const downloadingId = ref<string | null>(null)

async function download(exportId: string, filename?: string | null) {
  downloadingId.value = `xlsx-${exportId}`
  try {
    await store.downloadFile(exportId, filename)
  } finally {
    downloadingId.value = null
  }
}

async function downloadArchive(exportId: string, filename?: string | null) {
  downloadingId.value = `zip-${exportId}`
  try {
    await store.downloadArchive(exportId, filename)
  } finally {
    downloadingId.value = null
  }
}

// ── Détail d'un dépôt ────────────────────────────────────────────────────────
const detailDialog = ref<HTMLDialogElement | null>(null)
const detail = ref<FactorExportDetail | null>(null)
const isLoadingDetail = ref(false)

async function openDetail(row: FactorExportRow) {
  detail.value = null
  isLoadingDetail.value = true
  detailDialog.value?.showModal()
  detail.value = await store.fetchExportDetail(row.id)
  isLoadingDetail.value = false
}

// ── Helpers d'affichage ──────────────────────────────────────────────────────
const dateTimeFmt = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' })

function formatDateTime(iso: string | null): string {
  return iso ? dateTimeFmt.format(new Date(iso)) : '—'
}

function formatDay(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function formatPeriod(row: { dateFrom: string | null; dateTo: string | null }): string {
  if (!row.dateFrom || !row.dateTo) return '—'
  if (row.dateFrom === row.dateTo) return formatDay(row.dateFrom)
  return `${formatDay(row.dateFrom)} → ${formatDay(row.dateTo)}`
}

function formatAmount(n: number | null): string {
  if (n == null) return '—'
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const STATUS_BADGES: Record<FactorInvoiceRowStatus, { label: string; cls: string }> = {
  NON_EXPORTE: { label: 'Non déposée', cls: 'badge-ghost' },
  EXPORTED: { label: 'Déposée', cls: 'badge-info' },
  ACCEPTED: { label: 'Acceptée', cls: 'badge-success' },
  REFUSED: { label: 'Refusée', cls: 'badge-error' },
}

const STATUS_FILTERS: Array<{ value: FactorInvoiceRowStatus | ''; label: string }> = [
  { value: '', label: 'Tous les statuts' },
  { value: 'NON_EXPORTE', label: 'Non déposées' },
  { value: 'EXPORTED', label: 'Déposées' },
  { value: 'ACCEPTED', label: 'Acceptées' },
  { value: 'REFUSED', label: 'Refusées' },
]

onMounted(() => {
  loadInvoices()
  store.fetchExports()
})
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <header>
      <h1 class="text-2xl font-bold">Dépôt BNP Factor</h1>
      <p class="mt-1 max-w-prose text-base-content/80">
        Factures réglées par affacturage <span class="font-medium">BNP Factor</span> : suivi du cycle de vie
        (dépôt, acceptation, financement, remboursement) et génération des fichiers de dépôt (Excel + archive PDF).
      </p>
    </header>

    <!-- ── Onglets ─────────────────────────────────────────────────────────── -->
    <div role="tablist" class="tabs tabs-bordered mt-6">
      <button
        role="tab"
        type="button"
        class="tab"
        :class="{ 'tab-active': activeTab === 'invoices' }"
        :aria-selected="activeTab === 'invoices'"
        @click="activeTab = 'invoices'"
      >
        Factures
      </button>
      <button
        role="tab"
        type="button"
        class="tab"
        :class="{ 'tab-active': activeTab === 'exports' }"
        :aria-selected="activeTab === 'exports'"
        @click="activeTab = 'exports'"
      >
        Dépôts générés
      </button>
    </div>

    <!-- ══ Onglet Factures ═══════════════════════════════════════════════════ -->
    <section v-show="activeTab === 'invoices'" class="mt-4" aria-label="Factures BNP Factor">
      <div class="card border border-base-300 bg-base-100 shadow-sm">
        <div class="card-body p-4 md:p-6">
          <!-- Filtres -->
          <div class="flex flex-wrap items-end gap-3">
            <div>
              <label class="label" for="inv-date-from"><span class="label-text">Facture du</span></label>
              <input id="inv-date-from" v-model="fDateFrom" type="date" class="input input-bordered tabular-nums" @change="applyFilters" />
            </div>
            <div>
              <label class="label" for="inv-date-to"><span class="label-text">au</span></label>
              <input id="inv-date-to" v-model="fDateTo" type="date" class="input input-bordered tabular-nums" @change="applyFilters" />
            </div>
            <div>
              <label class="label" for="inv-status"><span class="label-text">Statut</span></label>
              <select id="inv-status" v-model="fStatus" class="select select-bordered" @change="applyFilters">
                <option v-for="s in STATUS_FILTERS" :key="s.value" :value="s.value">{{ s.label }}</option>
              </select>
            </div>
            <div class="grow">
              <label class="label" for="inv-search"><span class="label-text">Recherche</span></label>
              <input
                id="inv-search"
                type="search"
                class="input input-bordered w-full"
                placeholder="N° de facture ou client…"
                :value="fQuery"
                @input="onSearchInput(($event.target as HTMLInputElement).value)"
              />
            </div>
            <button
              type="button"
              class="btn btn-ghost"
              :disabled="store.isLoadingInvoices"
              :aria-busy="store.isLoadingInvoices"
              @click="loadInvoices"
            >
              <ArrowPathIcon class="h-5 w-5" :class="{ 'animate-spin motion-reduce:animate-none': store.isLoadingInvoices }" aria-hidden="true" />
              Rafraîchir
            </button>
          </div>

          <div v-if="store.isLoadingInvoices && !store.invoices.length" class="py-8 text-center">
            <span class="loading loading-spinner" aria-hidden="true"></span>
            <p class="mt-2 text-base-content/80">Chargement des factures…</p>
          </div>

          <p v-else-if="!store.invoices.length" class="py-8 text-center text-base-content/80">
            Aucune facture BNP Factor pour ces critères.
          </p>

          <div v-else class="-mx-4 mt-2 overflow-x-auto md:mx-0">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col">Code client</th>
                  <th scope="col">N° facture</th>
                  <th scope="col">Date facture</th>
                  <th scope="col">Échéance</th>
                  <th scope="col" class="text-right">Montant</th>
                  <th scope="col">Statut factor</th>
                  <th scope="col">Suivi</th>
                  <th scope="col"><span class="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="inv in store.invoices" :key="inv.invoiceUuid">
                  <td class="font-medium">{{ inv.companyName ?? '—' }}</td>
                  <td class="tabular-nums">{{ inv.invoiceName ?? '—' }}</td>
                  <td class="whitespace-nowrap tabular-nums">{{ formatDay(inv.invoiceDate) }}</td>
                  <td class="whitespace-nowrap tabular-nums">{{ formatDay(inv.invoiceDateDue) }}</td>
                  <td class="whitespace-nowrap text-right tabular-nums">
                    {{ formatAmount(inv.amount) }} {{ inv.currencyCode ?? 'EUR' }}
                  </td>
                  <td>
                    <span class="badge badge-sm" :class="STATUS_BADGES[inv.factorStatus]?.cls">
                      {{ STATUS_BADGES[inv.factorStatus]?.label ?? inv.factorStatus }}
                    </span>
                  </td>
                  <td>
                    <div class="flex flex-wrap gap-1">
                      <span v-if="inv.fundedByFactorAt" class="badge badge-sm badge-success badge-outline" :title="`Financé le ${formatDay(inv.fundedByFactorAt)}`">Financé</span>
                      <span v-if="inv.factorSettledAt" class="badge badge-sm badge-neutral badge-outline" :title="`Soldé le ${formatDay(inv.factorSettledAt)}`">Soldé</span>
                      <span v-if="inv.refusalReason" class="text-sm text-base-content/70" :title="inv.refusalReason">{{ inv.refusalReason }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="flex justify-end">
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        :disabled="inv.factorStatus === 'NON_EXPORTE'"
                        :aria-label="`Suivi factor de ${inv.invoiceName ?? 'la facture'}`"
                        :title="inv.factorStatus === 'NON_EXPORTE' ? 'Facture non encore déposée' : 'Saisir le suivi factor'"
                        @click="openLifecycle(inv)"
                      >
                        <PencilSquareIcon class="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="store.invoices.length" class="mt-4 flex flex-wrap items-center justify-between gap-3">
            <span class="text-sm text-base-content/70 tabular-nums">{{ rangeFrom }}–{{ rangeTo }} sur {{ store.invoicesTotal }}</span>
            <div class="join">
              <button type="button" class="btn btn-sm join-item" :disabled="page <= 1" @click="setPage(page - 1)">←</button>
              <span class="btn btn-sm join-item pointer-events-none tabular-nums">{{ page }} / {{ pageCount }}</span>
              <button type="button" class="btn btn-sm join-item" :disabled="page >= pageCount" @click="setPage(page + 1)">→</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ══ Onglet Dépôts générés ═════════════════════════════════════════════ -->
    <section v-show="activeTab === 'exports'" class="mt-4" aria-label="Dépôts générés">
      <!-- Générer -->
      <div class="card border border-base-300 bg-base-100 shadow-sm" aria-labelledby="generate-title">
        <div class="card-body p-4 md:p-6">
          <h2 id="generate-title" class="card-title text-lg font-semibold">Générer un dépôt</h2>
          <p class="max-w-prose text-base-content/80">
            Sélectionne les factures émises non réglées (mode BNP Factor) dont la date de facture est dans la plage, en
            excluant celles déjà déposées (anti-doublon). Produit le fichier Excel de dépôt + une archive ZIP des PDF.
          </p>

          <form class="mt-3 flex flex-wrap items-end gap-4" @submit.prevent="generate">
            <div>
              <label class="label" for="gen-date-from"><span class="label-text">Du</span></label>
              <input
                id="gen-date-from"
                v-model="gDateFrom"
                type="date"
                class="input input-bordered tabular-nums"
                :class="{ 'input-error': rangeInvalid && gDateFrom && gDateTo }"
                required
              />
            </div>
            <div>
              <label class="label" for="gen-date-to"><span class="label-text">Au (inclus)</span></label>
              <input
                id="gen-date-to"
                v-model="gDateTo"
                type="date"
                class="input input-bordered tabular-nums"
                :class="{ 'input-error': rangeInvalid && gDateFrom && gDateTo }"
                required
              />
            </div>
            <button type="submit" class="btn btn-primary" :disabled="rangeInvalid || store.isGenerating" :aria-busy="store.isGenerating">
              <span v-if="store.isGenerating" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
              <DocumentPlusIcon v-else class="h-5 w-5" aria-hidden="true" />
              {{ store.isGenerating ? 'Génération en cours…' : 'Générer le dépôt' }}
            </button>
          </form>
          <p v-if="rangeInvalid && gDateFrom && gDateTo" class="mt-1 text-sm text-error">
            La date de début doit être antérieure ou égale à la date de fin.
          </p>

          <label class="mt-2 flex w-fit cursor-pointer items-center gap-2">
            <input v-model="forceReexport" type="checkbox" class="checkbox checkbox-sm" />
            <span class="label-text">Re-déposer les factures déjà incluses dans un dépôt précédent</span>
          </label>

          <div v-if="generateError" role="alert" class="alert alert-error mt-4">
            <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{{ generateError }}</span>
          </div>

          <!-- Résultat -->
          <div v-if="result" role="status" class="mt-4 rounded-box border border-base-300 bg-base-200 p-4">
            <h3 class="flex items-center gap-2 font-semibold">
              <CheckCircleIcon class="h-5 w-5 text-success" aria-hidden="true" />
              Résultat — du {{ formatDay(result.period.from) }} au {{ formatDay(result.period.to) }}
            </h3>
            <p class="mt-2 text-base-content/80">
              {{ result.count }} facture{{ result.count > 1 ? 's' : '' }} déposée{{ result.count > 1 ? 's' : '' }} —
              total {{ formatAmount(result.totalAmount) }} €.
            </p>
            <p v-if="result.count === 0" class="mt-1 text-base-content/80">
              Aucune facture éligible sur la période (déjà déposées ou aucune facture BNP Factor non réglée).
            </p>

            <div v-if="result.exportId" class="mt-3 flex flex-wrap gap-2">
              <button type="button" class="btn btn-outline btn-sm" :disabled="downloadingId === `xlsx-${result.exportId}`" @click="download(result.exportId!, result.filename)">
                <ArrowDownTrayIcon class="h-4 w-4" aria-hidden="true" />
                Télécharger le fichier (.xlsx)
              </button>
              <button
                v-if="result.archiveFilename"
                type="button"
                class="btn btn-outline btn-sm"
                :disabled="downloadingId === `zip-${result.exportId}`"
                @click="downloadArchive(result.exportId!, result.archiveFilename)"
              >
                <ArchiveBoxIcon class="h-4 w-4" aria-hidden="true" />
                Archive PDF (.zip)
              </button>
            </div>

            <div v-if="result.missingPdfs.length" role="alert" class="alert alert-warning mt-3">
              <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
              <span>
                {{ result.missingPdfs.length }} PDF non récupéré{{ result.missingPdfs.length > 1 ? 's' : '' }}
                (facture{{ result.missingPdfs.length > 1 ? 's' : '' }} non éditée{{ result.missingPdfs.length > 1 ? 's' : '' }} dans Odoo) :
                {{ result.missingPdfs.slice(0, 10).join(', ') }}{{ result.missingPdfs.length > 10 ? '…' : '' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Historique -->
      <div class="card mt-6 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="history-title">
        <div class="card-body p-4 md:p-6">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <h2 id="history-title" class="card-title text-lg font-semibold">
              Historique des dépôts
              <span v-if="store.exports.length" class="badge badge-sm badge-ghost tabular-nums">{{ store.exports.length }}</span>
            </h2>
            <button type="button" class="btn btn-ghost" :disabled="store.isLoadingExports" :aria-busy="store.isLoadingExports" @click="store.fetchExports()">
              <ArrowPathIcon class="h-5 w-5" :class="{ 'animate-spin motion-reduce:animate-none': store.isLoadingExports }" aria-hidden="true" />
              Rafraîchir
            </button>
          </div>

          <div v-if="store.isLoadingExports && !store.exports.length" class="py-8 text-center">
            <span class="loading loading-spinner" aria-hidden="true"></span>
          </div>

          <p v-else-if="!store.exports.length" class="py-8 text-center text-base-content/80">
            Aucun dépôt généré pour le moment.
          </p>

          <div v-else class="-mx-4 mt-2 overflow-x-auto md:mx-0">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col">Généré le</th>
                  <th scope="col">Période</th>
                  <th scope="col" class="text-right">Factures</th>
                  <th scope="col" class="text-right">Montant</th>
                  <th scope="col">Déclenché par</th>
                  <th scope="col"><span class="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="e in store.exports" :key="e.id">
                  <td class="whitespace-nowrap tabular-nums">{{ formatDateTime(e.createdAt) }}</td>
                  <td class="whitespace-nowrap tabular-nums">{{ formatPeriod(e) }}</td>
                  <td class="text-right tabular-nums">{{ e.lineCount }}</td>
                  <td class="whitespace-nowrap text-right tabular-nums">{{ formatAmount(e.totalAmount) }} €</td>
                  <td class="text-sm text-base-content/70">{{ e.triggeredBy ?? '—' }}</td>
                  <td>
                    <div class="flex justify-end gap-1">
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        :disabled="!e.hasFile || downloadingId === `xlsx-${e.id}`"
                        :aria-label="`Télécharger le fichier ${e.filename ?? ''}`"
                        title="Télécharger le fichier (.xlsx)"
                        @click="download(e.id, e.filename)"
                      >
                        <ArrowDownTrayIcon class="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        :disabled="!e.hasArchive || downloadingId === `zip-${e.id}`"
                        :aria-label="`Télécharger l'archive PDF`"
                        title="Télécharger l'archive PDF (.zip)"
                        @click="downloadArchive(e.id, e.archiveFilename)"
                      >
                        <ArchiveBoxIcon class="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        :aria-label="`Voir le détail du dépôt du ${formatDateTime(e.createdAt)}`"
                        title="Voir le détail"
                        @click="openDetail(e)"
                      >
                        <EyeIcon class="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Dialog cycle de vie ───────────────────────────────────────────────── -->
    <FactorLifecycleDialog
      :invoice="lifecycleTarget"
      :loading="isSavingLifecycle"
      @close="lifecycleTarget = null"
      @save="onLifecycleSave"
    />

    <!-- ── Modal détail d'un dépôt ───────────────────────────────────────────── -->
    <dialog ref="detailDialog" class="modal">
      <div class="modal-box max-w-3xl">
        <h3 class="text-lg font-semibold">Détail du dépôt</h3>

        <div v-if="isLoadingDetail" class="py-8 text-center">
          <span class="loading loading-spinner" aria-hidden="true"></span>
        </div>

        <template v-else-if="detail">
          <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            <div>
              <dt class="text-sm text-base-content/70">Fichier</dt>
              <dd class="font-medium break-all">{{ detail.filename ?? '—' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-base-content/70">Période</dt>
              <dd class="tabular-nums">{{ formatPeriod(detail) }}</dd>
            </div>
            <div>
              <dt class="text-sm text-base-content/70">Factures</dt>
              <dd class="tabular-nums">{{ detail.lineCount }}</dd>
            </div>
            <div>
              <dt class="text-sm text-base-content/70">Montant total</dt>
              <dd class="tabular-nums">{{ formatAmount(detail.totalAmount) }} €</dd>
            </div>
          </dl>

          <div class="mt-4 max-h-96 overflow-y-auto overflow-x-auto">
            <table class="table table-sm">
              <thead class="sticky top-0 bg-base-100">
                <tr>
                  <th scope="col">Facture</th>
                  <th scope="col">Client</th>
                  <th scope="col">Date</th>
                  <th scope="col" class="text-right">Montant</th>
                  <th scope="col">Statut</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="l in detail.lines" :key="l.invoiceUuid">
                  <td class="font-medium tabular-nums">{{ l.invoiceName ?? '—' }}</td>
                  <td>{{ l.companyName ?? '—' }}</td>
                  <td class="whitespace-nowrap tabular-nums">{{ formatDay(l.invoiceDate) }}</td>
                  <td class="whitespace-nowrap text-right tabular-nums">{{ formatAmount(l.amount) }} {{ l.currencyCode ?? 'EUR' }}</td>
                  <td>
                    <span class="badge badge-sm" :class="STATUS_BADGES[l.factorStatus]?.cls">
                      {{ STATUS_BADGES[l.factorStatus]?.label ?? l.factorStatus }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="modal-action">
            <form method="dialog">
              <button class="btn btn-ghost">Fermer</button>
            </form>
          </div>
        </template>

        <p v-else class="py-8 text-center text-base-content/80">Impossible de charger le détail.</p>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button>Fermer</button>
      </form>
    </dialog>
  </div>
</template>
